import type { ObjUnion } from '@blockworks/platform';
import type { IconComponent } from '@blockworks/ui';

import { MergedVisualizationExecutionData } from '../components/visualization';

type SimpleVisualization = ObjUnion<
    Pick<
        MergedVisualizationExecutionData,
        'config' | 'title' | 'queryId' | 'type' | 'description' | 'id' | 'subTitle' | 'slug'
    >
>;

type VisualizationQuickAction = {
    icon: IconComponent;
    label: string;
    callback: ({ visualization }: { visualization: SimpleVisualization }) => void;
};

type VisualizationFeatures = {
    /** Controls the actionable error messages that appear when a visualization fails to load */
    verboseErrorMessages?: boolean;
    /** Controls the ability to navigate between pages in a table visualization */
    tablePagination?: boolean;
    /**
     * Removes the gate (aka subscribe-to-access message) for the data export feature
     *
     * "dangerously" prefix:  Used for internal logic. Be cautious when using these as there may be unintended side effects.
     */
    dangerouslyGateDataExport?: boolean;
    /**
     * Enables the use of an endpoint proxy to make visualization/execution requests
     * */
    getExecutionProxyUri?: ({ visualizationId }: { visualizationId: string }) => string;
    /**
     * Enables the ability to download a screenshot without branding and styles e.g. (no wordmark, no background)
     *
     * "dangerously" prefix:  Used for internal logic. Be cautious when using these as there may be unintended side effects.
     **/
    dangerouslyAllowUnbrandedScreenshotDownloads?: boolean;
    quickActions?: VisualizationQuickAction[];
    /** Enable share on social media buttons */
    socialMediaSharing?:
        | {
              message?: string;
              callback: (
                  params: { action: string; visualization: SimpleVisualization; image?: Blob },
                  ctx: { setMessage: (message: string) => void },
              ) => void;
          }
        | false;
};

type VisualizationLayoutOptions = {
    controlSize?: 'xxs' | 'xs';
    defaultControl?: 'toggle' | 'dropdown';
    hideLegend?: boolean;
    headerHeight?: number;
};

const DEFAULT_FEATURES_CONFIG: VisualizationFeatures = {
    verboseErrorMessages: false,
    tablePagination: false,
    dangerouslyAllowUnbrandedScreenshotDownloads: false,
    dangerouslyGateDataExport: undefined,
    getExecutionProxyUri: undefined,
    quickActions: undefined,
} satisfies VisualizationFeatures;

const HAS_ALL_FEATURES_CONFIG = {
    verboseErrorMessages: false,
    tablePagination: true,
    dangerouslyAllowUnbrandedScreenshotDownloads: false,
    dangerouslyGateDataExport: false,
    getExecutionProxyUri: undefined,
    quickActions: undefined,
} satisfies VisualizationFeatures;

const DEFAULT_LAYOUT_OPTIONS = {
    controlSize: 'xxs',
    defaultControl: 'toggle',
    hideLegend: false,
    headerHeight: 40,
} satisfies VisualizationLayoutOptions;

export type { VisualizationFeatures, VisualizationLayoutOptions, VisualizationQuickAction };
export { DEFAULT_FEATURES_CONFIG, DEFAULT_LAYOUT_OPTIONS, HAS_ALL_FEATURES_CONFIG };
